import {cloneDeep} from 'lodash'
import axios from 'axios'
import {ModuleFc} from '@/project'
import {
  TaxRefundState,
  TaxRefundResponse,
  SurveyKeyResponse,
  UpdateBankAccountPayload,
  UploadSignatureImagePayload,
  // UploadSignatureImagePayloadCamel,
  UpdateSignaturePayload,
  GetPresignedUrlForSignaturePayload,
  GetPresignedUrlForSignatureResponse,
  GetContractPayload,
  RefundStatus,
} from './types'
import {resultToTaxRefund} from './utils'
import {DateTime} from 'luxon'
import {EventProperty} from '../gtm/types'

export {TaxRefundState}

const initTaxRefund = {
  bizNo: null,
  refund: 0,
  refundDeclareYear: null,
  refundStartYear: null,
  refundEndYear: null,
  salesPerYear: [],
  salesExpected: 0,
  taxPerYear: [],
  taxExpected: 0,
  refundPerYear: [],
  refundExpected: 0,
}

const initState: TaxRefundState = {
  id: '',
  status: 1,
  errorCode: '',
  errorMessage: '',
  taxrefund: initTaxRefund,
  confirmationStep: 0,
  createdAt: DateTime.local(),
  surveyKey: '',
  commissionProposal: 0.3,
}

const module: ModuleFc<TaxRefundState> = (ctx) => ({
  namespaced: true,
  state: cloneDeep(initState),
  getters:{
    getId(state) {
      return state.id
    },
    getSurveyKey(state) {
      return state.surveyKey
    },
    getRefundStatus(state) {
      switch(state.status) {
        case RefundStatus.환급급조회전:
          return RefundStatus.환급급조회전
        case RefundStatus.환급급조회중:
          return RefundStatus.환급급조회중
        case RefundStatus.환급급확인전:
          return RefundStatus.환급급확인전
        case RefundStatus.환급급확인:
          return RefundStatus.환급급확인
        case RefundStatus.환급급신청:
          return RefundStatus.환급급신청
        case RefundStatus.기장상담신청:
          return RefundStatus.기장상담신청
        case RefundStatus.설문작성대기:
          return RefundStatus.설문작성대기
        case RefundStatus.확정진단중:
          return RefundStatus.확정진단중
        case RefundStatus.접수준비중:
          return RefundStatus.접수준비중
        case RefundStatus.국세청환급대기:
          return RefundStatus.국세청환급대기
        case RefundStatus.환급완료:
          return RefundStatus.환급완료
        default:
          return RefundStatus.에러
      }
    },
    isBeforeConfirm(state) {
      return state.status === RefundStatus.환급급확인전
    },
    isConfirm(state) {
      return state.status > RefundStatus.환급급확인전
    },
    isNotFound(state) {
      if(state.taxrefund) {
        return state.taxrefund.refund === 0
      }
      return false
    },
    getRefundError(state) {
      return {
        code: state.errorCode,
        message: state.errorMessage
      }
    },
    eventProperties(_, getters): EventProperty {
      return {
        'workflow': 'tax_refund',
        'status': getters.getStatus,
        'taxrefund': getters.taxrefund
      }
    }
  },
  mutations: {
    updateTaxRefund(state, payload: {data?: TaxRefundResponse}) {
      state.id = payload?.data?.id || ''
      state.status = payload?.data?.status || 0
      state.errorCode = payload?.data?.errorCode || ''
      state.errorMessage = payload?.data?.errorMessage || ''
      state.confirmationStep = (state.status > 2) ? 3 : 0
      state.taxrefund = resultToTaxRefund(payload?.data?.result || initTaxRefund)
      state.commissionProposal = payload?.data?.commissionProposal || 0.3
    },
    updateConfirmation(state, payload: number) {
      state.confirmationStep = payload
    },
    updateSurveyKey(state, payload: {data?: SurveyKeyResponse}) {
      state.surveyKey = payload?.data?.sessionKey || ''
    },
  },
  actions: {
    async getTaxRefund({commit, rootState}) {
      try {
        const result = await ctx.$axios({
          url: '/taxrefund/',
          method: 'get',
          data: {
            bizId: rootState.biz.id
          }
        })
        commit('updateTaxRefund', result)
      } catch(err) {
        commit('updateTaxRefund', null)
      }
    },
    async postSurveyKey({commit, rootState}) {
      try {
        const result = await ctx.$axios({
          url: '/surveyKey/',
          method: 'get',
          data: {
            bizId: rootState.biz.id,
            baseYear: rootState.taxrefund.taxrefund?.refundDeclareYear,
          }
        })

        commit('updateSurveyKey', result)
      } catch(err) {
        commit('updateSurveyKey', null)
      }
    },
    async runConfirm({commit, rootState}) {
      try {
        await ctx.$axios({
          url: '/taxrefund/',
          method: 'post',
          data: {
            bizId: rootState.biz.id,
            status: 3
          }
        })
      } catch(err) {
        commit('updateTaxRefund', null)
      }
    },
    async runContact({commit, rootState}, isConsulting: boolean) {
      try {
        await ctx.$axios({
          url: '/taxrefund/',
          method: 'post',
          data: {
            bizId: rootState.biz.id,
            status: isConsulting ? 5 : 4
          }
        })
      } catch(err) {
        commit('updateTaxRefund', null)
      }
    },
    async updateBankAccount(_, payload: UpdateBankAccountPayload) {
      const {taxRefundId, bizId, bankAccountName, bankName, bankOwnerName} = payload
      const res = await ctx.$axios({
        url: `/bizs/${bizId}/bank_account/${taxRefundId}`,
        method: 'put',
        data: {
          bank_name: bankName,
          bank_owner_name: bankOwnerName,
          bank_account_number: bankAccountName,
        },
      })
      return res.data
    },
    async getPresignedUrlForSignature(_, payload: GetPresignedUrlForSignaturePayload):
    Promise<GetPresignedUrlForSignatureResponse> {
      const {taxRefundId, bizId} = payload
      const res = await ctx.$axios({
        url: `/bizs/${bizId}/signature/${taxRefundId}`,
        method: 'get',
      })
      return res.data
    },
    async uploadSignatureImage(_, payload: UploadSignatureImagePayload) {
      const {presignedUrl, file} = payload
      const formData = new FormData()
      formData.append('key', presignedUrl.fields.key)
      formData.append('x-amz-algorithm', presignedUrl.fields.xAmzAlgorithm)
      formData.append('x-amz-credential', presignedUrl.fields.xAmzCredential)
      formData.append('x-amz-date', presignedUrl.fields.xAmzDate)
      formData.append('x-amz-signature', presignedUrl.fields.xAmzSignature)
      formData.append('policy', presignedUrl.fields.policy)
      formData.append('file', file)
      const res = await axios({
        headers: {
          'Content-type': 'multipart/form-data',
        },
        url: presignedUrl.url,
        method: 'post',
        data: formData,
      })
      return res
    },
    async updateSignature(_, payload: UpdateSignaturePayload) {
      const {taxRefundId, presignedUrlKey, bizId} = payload
      const res = await ctx.$axios({
        url: `/bizs/${bizId}/signature/${taxRefundId}`,
        method: 'post',
        data: {presigned_url_key : presignedUrlKey}
      })
      return res.data
    },
    async getContract(_, payload: GetContractPayload) {
      const {taxRefundId, bizId} = payload
      const res = await ctx.$axios({
        url: `/bizs/${bizId}/contract/${taxRefundId}`,
        method: 'get',
      })
      return res.data
    },
    goToFirstConfirmationStep({commit}) {
      commit('updateConfirmation', 0)
    },
    nextConfirmationStep({commit, state}) {
      if(state.confirmationStep < 4) {
        commit('updateConfirmation', state.confirmationStep + 1)
      }
    },
    prevConfirmationStep({commit, state}) {
      if(state.confirmationStep > 0) {
        commit('updateConfirmation', state.confirmationStep - 1)
      }
    }
  },
})

export default module
